import { Alert, Badge, Button, Card, Dialog, OtpField, Outcome, PageHeading, PasswordField, TotpSetup, useToast } from '@aulaviva/ui';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTotpAvvia, useTotpConferma, useTotpDisattiva } from '@/api/amministrazione';
import { ErroreRichiesta } from '@/api/client';
import { useUtenteArea } from '@/layout/utente';
import { comuni, sicurezza } from '@/testi';
import { ITuoiDati } from './componenti/ITuoiDati';

/** Secondo fattore (TOTP) e i tuoi dati, per relatore, regia e amministratore. */
export function Sicurezza() {
  const t = sicurezza;
  const utente = useUtenteArea();
  const [params] = useSearchParams();
  const toast = useToast();
  const avvia = useTotpAvvia();
  const conferma = useTotpConferma();
  const disattiva = useTotpDisattiva();
  const [codice, setCodice] = useState('');
  const [erroreCodice, setErroreCodice] = useState<string | null>(null);
  const [dialogoDisattiva, setDialogoDisattiva] = useState(false);
  const [password, setPassword] = useState('');
  const [codiceDisattiva, setCodiceDisattiva] = useState('');
  const [erroreDisattiva, setErroreDisattiva] = useState<string | null>(null);
  const setupAperto = avvia.isSuccess && !utente.totpAttivo;
  const richiesto = utente.totpRichiesto;

  async function confermaCodice() {
    setErroreCodice(null);
    if (codice.length !== 6) {
      setErroreCodice(t.codiceAutenticazione.aiuto);
      return;
    }
    try {
      await conferma.mutateAsync(codice);
      setCodice('');
      avvia.reset();
      toast.mostra({ variant: 'success', title: t.totp.attivato });
    } catch (e) {
      setErroreCodice(e instanceof ErroreRichiesta && e.status < 500 ? t.totp.codiceErrato : e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  async function confermaDisattiva() {
    setErroreDisattiva(null);
    try {
      await disattiva.mutateAsync({ password, codice: codiceDisattiva });
      setDialogoDisattiva(false);
      setPassword('');
      setCodiceDisattiva('');
      toast.mostra({ variant: 'success', title: t.totp.disattivato });
    } catch (e) {
      setErroreDisattiva(e instanceof ErroreRichiesta && e.status < 500 ? t.totp.codiceErrato : e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  return (
    <>
      <PageHeading subtitle={t.sottotitolo}>{t.titolo}</PageHeading>
      {richiesto && params.get('motivo') === 'totp' && (
        <Outcome variant="warning" title={t.guardAdmin.titolo}>
          {t.guardAdmin.testo}
        </Outcome>
      )}
      <Card className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="t-title-2">{t.totp.titolo}</h2>
          {utente.totpAttivo ? (
            <Badge variant="success" icon={ShieldCheck}>
              {t.totp.attivo}
            </Badge>
          ) : (
            <Badge variant={richiesto ? 'warning' : 'neutral'} icon={ShieldOff}>
              {t.totp.nonAttivo}
            </Badge>
          )}
        </div>
        {richiesto && !setupAperto && <p className="t-body text-ink-muted">{t.totp.richiesto}</p>}
        {utente.totpAttivo && (
          <Button variant="discreet" icon={ShieldOff} onClick={() => setDialogoDisattiva(true)} className="self-start">
            {t.totp.disattiva}
          </Button>
        )}
        {!utente.totpAttivo && !setupAperto && (
          <>
            {avvia.isError && <Alert variant="error" title={avvia.error.message} />}
            <Button variant="primary" icon={ShieldCheck} onClick={() => avvia.mutate()} loading={avvia.isPending} loadingText={comuni.caricamento} className="self-start">
              {t.totp.attiva}
            </Button>
          </>
        )}
        {setupAperto && avvia.data && (
          <TotpSetup qrDataUrl={avvia.data.qrDataUrl} segreto={avvia.data.segreto} codice={codice} onCodice={setCodice} onConferma={confermaCodice} loading={conferma.isPending} error={erroreCodice ?? undefined} />
        )}
      </Card>
      <ITuoiDati primario={utente.totpAttivo} />
      <Dialog
        open={dialogoDisattiva}
        onOpenChange={(o) => !o && setDialogoDisattiva(false)}
        title={t.totp.disattivaTitolo}
        description={t.totp.disattivaTesto}
        actions={
          <>
            <Button onClick={() => setDialogoDisattiva(false)}>{t.totp.annulla}</Button>
            <Button variant="primary" onClick={confermaDisattiva} loading={disattiva.isPending} loadingText={comuni.caricamento}>
              {t.totp.disattivaConferma}
            </Button>
          </>
        }
      >
        {erroreDisattiva && <Alert variant="error" title={erroreDisattiva} />}
        <PasswordField label={t.totp.password} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <OtpField label={t.totp.codice} value={codiceDisattiva} onChange={setCodiceDisattiva} />
      </Dialog>
    </>
  );
}
